import { useSessionStorage } from "@uidotdev/usehooks";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
//@ts-ignore
import { tw } from "@careinfo/tailwind";
import APIS from "../../constant/Endpoint";
import type { IFilterBranch } from "../../types/interface";
import { hasValues } from "../../utils/hasValues";
import Card from "../Reusable/Card";
import DataTable from "../Reusable/DataTable";
import NACell from "../Reusable/DataTable/NACell";
import BranchFilter from "./BranchFilter";
import { getKey } from "../../helpers/sessionKey";

const BranchInvoice = () => {
  const navigate = useNavigate();
  const [localState, handleSetState] = useSessionStorage<IFilterBranch>(
    "branch-invoice-filter",
    {
      created__range: null,
      pickup_branch: null,
      to_branch: null,
      status__in: null,
    }
  );
  const [isFilterVisible, setFilterVisiable] = useState(hasValues(localState));

  const { setValue, getValues, watch, reset, control } = useForm<IFilterBranch>(
    {
      defaultValues: async () => ({
        created__range: localState?.created__range ?? null,
        pickup_branch: localState?.pickup_branch ?? null,
        to_branch: localState?.to_branch ?? null,
        status__in: localState?.status__in ?? null,
      }),
    }
  );

  const handleFilterReset = () => {
    reset({
      created__range: null,
      pickup_branch: null,
      to_branch: null,
      status__in: null,
    });
  };

  const defaultColumns: any = useMemo(() => {
    return [
      {
        header: "Statement ID",
        accessorKey: "invoice_id",
        cell: ({ row, getValue }: any) => {
          return (
            <>
              <button
                className={tw`cursor-pointer whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                onClick={() => navigate(`preview/${row?.original?.id}`)}
              >
                {getValue() ?? "N/A"}
              </button>
            </>
          );
        },
      },
      {
        header: "Date Range",
        accessorKey: "date_range",
        cell: ({ getValue }: any) => {
          const value = getValue();
          if (!value) return <p className={tw`whitespace-nowrap`}>N/A</p>;

          try {
            const parsed = JSON.parse(value);
            const from = parsed?.lower
              ? moment(parsed.lower).format("YYYY-MM-DD")
              : "N/A";
            const to = parsed?.upper
              ? moment(parsed.upper).format("YYYY-MM-DD")
              : "N/A";

            return (
              <p className={tw`whitespace-nowrap`}>
                {from}
                <span className={tw`mx-1`}>-</span>
                {to}
              </p>
            );
          } catch (e) {
            return <p className={tw`whitespace-nowrap`}>N/A</p>;
          }
        },
      },
      {
        header: "From Branch",
        accessorKey: "from_branch.name",
        cell: ({ getValue }: any) => {
          return <p>{getValue() ?? "All"}</p>;
        },
      },
      {
        header: "To Branch",
        accessorKey: "to_branch.name",
        cell: ({ getValue }: any) => {
          return <p>{getValue() ?? "All"}</p>;
        },
      },
      {
        header: "Paid Date",
        accessorKey: "settlement_date",
        cell: ({ getValue }: any) => {
          return (
            <p className={tw`text-sm whitespace-nowrap`}>
              {getValue()
                ? moment(getValue()).format("YY-MM-DD HH:mm:ss")
                : "N/A"}
            </p>
          );
        },
      },
      {
        header: "COD",
        accessorKey: "total_charge.total_cod",
        cell: ({ getValue }: any) => {
          return (
            <>
              <p className={tw`whitespace-nowrap`}>{getValue() ?? 0.0}</p>
            </>
          );
        },
      },
      {
        header: "Coll COD",
        accessorKey: "total_charge.collected",
        cell: ({ getValue }: any) => {
          return (
            <>
              <p className={tw`whitespace-nowrap`}>{getValue() ?? 0.0}</p>
            </>
          );
        },
      },
      {
        header: "Branch Rate",
        accessorKey: "branch_rate",
        cell: ({ getValue }: any) => {
          return (
            <>
              <p className={tw`whitespace-nowrap`}>{getValue() ?? 0.0}</p>
            </>
          );
        },
      },
      {
        header: "Payable",
        accessorKey: "invoice_amount",
        cell: ({ getValue }: any) => {
          return (
            <>
              <p className={tw`whitespace-nowrap`}>{getValue() ?? 0.0}</p>
            </>
          );
        },
      },
      {
        header: "Transit Charge",
        accessorKey: "transit_charge",
        cell: ({ getValue }: any) => {
          return (
            <>
              <p className={tw`whitespace-nowrap`}>{getValue() ?? 0.0}</p>
            </>
          );
        },
      },
      {
        header: "Payment Remarks",
        accessorKey: "remarks",
        cell: NACell,
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }: any) => {
          return (
            <>
              <p className={tw`whitespace-nowrap uppercase`}>{getValue()}</p>
            </>
          );
        },
      },
    ];
  }, []);

  const defaultCargoColumns: any = useMemo(() => {
    return [
      {
        header: "Statement ID",
        accessorKey: "invoice_id",
        cell: ({ row, getValue }: any) => {
          return (
            <>
              <button
                className={tw`cursor-pointer whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ring-offset-1`}
                onClick={() => navigate(`cargo-preview/${row?.original?.id}`)}
              >
                {getValue() ?? "N/A"}
              </button>
            </>
          );
        },
      },
      {
        header: "Date Range",
        accessorKey: "date_range",
        cell: ({ getValue }: any) => {
          const value = getValue();
          if (!value) return <p className={tw`whitespace-nowrap`}>N/A</p>;

          try {
            const parsed = JSON.parse(value);
            const from = parsed?.lower
              ? moment(parsed.lower).format("YYYY-MM-DD")
              : "N/A";
            const to = parsed?.upper
              ? moment(parsed.upper).format("YYYY-MM-DD")
              : "N/A";

            return (
              <p className={tw`whitespace-nowrap`}>
                {from}
                <span className={tw`mx-1`}>-</span>
                {to}
              </p>
            );
          } catch (e) {
            return <p className={tw`whitespace-nowrap`}>N/A</p>;
          }
        },
      },
      {
        header: "From Branch",
        accessorKey: "from_branch.name",
        cell: ({ getValue }: any) => {
          return <p>{getValue() ?? "All"}</p>;
        },
      },
      {
        header: "To Branch",
        accessorKey: "to_branch.name",
        cell: ({ getValue }: any) => {
          return <p>{getValue() ?? "All"}</p>;
        },
      },
      {
        header: "Paid Date",
        accessorKey: "settlement_date",
        cell: ({ getValue }: any) => {
          return (
            <p className={tw`text-sm whitespace-nowrap`}>
              {getValue()
                ? moment(getValue()).format("YY-MM-DD HH:mm:ss")
                : "N/A"}
            </p>
          );
        },
      },
      {
        header: "Branch Rate",
        accessorKey: "branch_rate",
        cell: ({ getValue }: any) => {
          return (
            <>
              <p className={tw`whitespace-nowrap`}>{getValue() ?? 0.0}</p>
            </>
          );
        },
      },
      {
        header: "Amount",
        accessorKey: "invoice_amount",
        cell: ({ getValue }: any) => {
          return (
            <>
              <p className={tw`whitespace-nowrap`}>{getValue() ?? 0.0}</p>
            </>
          );
        },
      },
      {
        header: "Payment Remarks",
        accessorKey: "remarks",
        cell: NACell,
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ getValue }: any) => {
          return (
            <>
              <p className={tw`whitespace-nowrap uppercase`}>{getValue()}</p>
            </>
          );
        },
      },
    ];
  }, []);

  const filterParams = watch();

  useEffect(() => {
    handleSetState({
      ...localState,
      ...filterParams,
      status__in: filterParams
        ? filterParams?.status__in?.map(
            (status: { label: string; value: string }) => ({
              ...status,
              id: status.value,
              name: status.label,
            })
          )
        : null,
    });
  }, [filterParams]);

  const params = useCallback(() => {
    const newValue = {};
    for (const [key, value] of Object.entries(filterParams)) {
      if (value) {
        if (Array.isArray(value)) {
          newValue[key] = value.map((v) => v.value).join(",");
        } else if (typeof value === "object") {
          newValue[key] = value.id;
        } else if (!value) {
          newValue[key] = null;
        } else {
          newValue[key] = value;
        }
      }
    }
    return newValue;
  }, [filterParams]);

  const clientType = getKey("client-type");

  return (
    <>
      <Card title="Filters" onClick={() => setFilterVisiable(!isFilterVisible)}>
        <BranchFilter
          isFilterVisible={isFilterVisible}
          setValue={setValue}
          getValues={getValues}
          handleFilterReset={handleFilterReset}
          control={control}
        />
      </Card>
      <DataTable
        url={APIS.BRANCH_INVOICE}
        queryKey={"Branch Invoice"}
        columns={clientType === "cargo" ? defaultCargoColumns : defaultColumns}
        addNewPagePath={"/create"}
        tableName={"branch-invoice"}
        orderSearch={true}
        params={params()}
      />
    </>
  );
};

export default BranchInvoice;
